import { Injectable, inject } from "@angular/core";
import { IgxGridComponent } from '@infragistics/igniteui-angular/grids/grid';
import { CachedRepositoryService } from "@tylertech/core-module";
import
{
    ODataApplyAggregate,
    ODataFilterFactoryService,
} from "@tylertech/odata-module";
import { Utilities } from "@tylertech/shared-module";
import
{
    ClassMetadata,
    IGridViewMetadata,
    MetadataSummary
} from "@tylertech/tyler-api-types";
import
{
    GridFilterService,
    GridMetadataService,
    ProviderStorageService,
} from "../../services";

@Injectable({ providedIn: "root" })
export class GridSummaryService
{
    private $odataFilterFactory = inject(ODataFilterFactoryService);
    private $providerStorageService = inject(ProviderStorageService);
    private $repositoryService = inject(CachedRepositoryService);
    private $gridMetadataService = inject(GridMetadataService);
    private $gridFilterService = inject(GridFilterService);


    setColumnSummaries(
        providerId: string,
        viewMetadata: IGridViewMetadata,
    ): void
    {
        const data = this.$providerStorageService.LocalEntitiesStore[providerId].filter(x => x["$inFilter"]);
        const summaryColumns = this.$gridMetadataService.getSummaryColumns(viewMetadata.GridProperties.ClassMetadata);
        if (summaryColumns.length === 0)
            return;
        const summaries = {};
        if (data)
            data.forEach(row =>
            {
                if (row.SerializedRowState !== "Deleted")
                {
                    const subPropertyEntity = row;
                    summaryColumns.forEach(name =>
                    {
                        const chain = name.split(".");
                        let childSubPropertyEntity = subPropertyEntity;
                        if (!summaries[chain[chain.length - 1]])
                            summaries[chain[chain.length - 1]] = 0;
                        for (let i = 0; i < chain.length - 1; i++)
                        {
                            const element = chain[i];
                            childSubPropertyEntity = childSubPropertyEntity[element];
                        }
                        const value = childSubPropertyEntity[chain[chain.length - 1]] ?? "";
                        summaries[chain[chain.length - 1]] += Number(value.toString()
                            .replace(/[^0-9.-]+/g, ""));
                    });
                }
            });
        this.$providerStorageService.ColumnSummaries[providerId] = summaries;
    }

    getColumnSummaries(
        providerId: string,
        grid: IgxGridComponent,
        viewMetadata: IGridViewMetadata,
        metadataServerPaging: boolean,
        summaryColumns: string[],
        loadRecent: boolean
    ): Promise<Record<string, number>>
    {
        if (metadataServerPaging)
            return this.getODataSummaries(grid, viewMetadata, summaryColumns, loadRecent, providerId);
        if (!Utilities.IsNullOrUndefinedOrEmptyObject(this.$providerStorageService.ColumnSummaries[providerId]))
            return Promise.resolve(this.$providerStorageService.ColumnSummaries[providerId]);
        return Promise.resolve({});
    }

    getODataSummaries(
        grid: IgxGridComponent,
        viewMetadata: IGridViewMetadata,
        summaryColumns: string[],
        loadRecent: boolean,
        providerId: string
    ): Promise<Record<string, number>>
    {
        const self = this;
        const recentIds = (loadRecent && this.$gridFilterService.allowShowRecent(viewMetadata)) ? this.$gridFilterService.recentIds(viewMetadata.GridId) : undefined;
        const odataFilterBuilder = this.$odataFilterFactory.createBuilder();
        this.$gridFilterService.applyGridFiltering(odataFilterBuilder, viewMetadata);
        const aggregates: ODataApplyAggregate[] = [];
        summaryColumns.forEach(column =>
        {
            aggregates.push({
                field: column.replace(/\./g, "/"),
                type: "sum",
                alias: `${column.substring(column.lastIndexOf(".") + 1)}Total`
            });
        });
        odataFilterBuilder.apply(aggregates);
        return this.$gridFilterService.buildOdataFilter(grid.filteringExpressionsTree, viewMetadata.GridProperties.ClassMetadata.Properties, recentIds, viewMetadata.GridProperties.IdProperty)
            .then(filter =>
            {
                return this.$repositoryService
                    .Fetch(viewMetadata.GridProperties.ClassMetadata.TypeName, odataFilterBuilder.build() + filter)
                    .then(result =>
                    {
                        if (result && result["value"] && result["value"].length === 1 && result["value"][0])
                        {
                            const summaries = {};
                            Object.keys(result["value"][0])
                                .forEach(key =>
                                {
                                    const field = key.substring(0, key.length - 5);
                                    if (key !== "$$TypeName" && field)
                                        summaries[field] = result["value"][0][key];
                                });
                            self.$providerStorageService.ColumnSummaries[providerId] = summaries;
                            return summaries;
                        }
                        return {};
                    });
            });
    }

    hasSummary(classMetadata: ClassMetadata): boolean
    {
        let hasSummary = false;
        classMetadata.Properties.forEach(property =>
        {
            if (property.ClassMetadata && this.hasSummary(property.ClassMetadata)) hasSummary = true;
            if (property.SummaryType === MetadataSummary.Sum) hasSummary = true;
        });
        return hasSummary;
    }
}
